/**
 * Mux consent gating - only enable Mux Data analytics when user has consented.
 * Replicates the gating logic from wme-component-video-top for adultswim context.
 *
 * Aligns with player-web (e.g. VideoController `buildConfig`): when WM exposes
 * `getAckTermsNeeded`, Mux stays off until it returns false (LTP / terms acknowledged).
 *
 * WBD LTP pop-ups use the same `getAckTermsNeeded` / `ackTerms()` flow; `ltp-consent-track-pending`
 * in sessionStorage is cleared when Mux is enabled (`clearLtpConsentTrackPending`), matching the
 * GEP/LTP script’s key name. `applyMuxWhenConsentReadyEpic` also listens for `ackTermsChanged`.
 *
 * Uses WM.UserConsent (player/embed) or WBD.UserConsent (adultswimdotcom) when available.
 *
 * When **both** are present (common on adultswim.com), we allow Mux if **either** stack grants
 * analytics categories. Otherwise WM can stay "deny" until late-loaded scripts hydrate while
 * WBD already reflects post-reload cookie consent — and Mux would never turn on.
 */

declare global {
    interface Window {
        /** Dev only: treat consent as granted for Mux (see module doc). */
        __ASVP_MUX_FORCE_CONSENT__?: boolean;
        WM?: {
            UserConsent?: {
                getRegion?: () => string;
                inUserConsentState?: (categories: string[]) => boolean;
                getUserConsentAdvertisingState?: () => boolean;
                getAckTermsNeeded?: () => boolean;
            };
        };
        WBD?: {
            UserConsent?: {
                getAckTermsNeeded?: () => boolean;
                isReady?: () => boolean;
                inUserConsentState?: (categories: string[]) => boolean;
            };
        };
    }
}

/** Survives `location.reload()` (in-memory `window.__ASVP_MUX_FORCE_CONSENT__` does not). */
export const SESSION_STORAGE_MUX_FORCE_CONSENT = 'asvp_mux_force_consent';

function readMuxForceConsentFromSession(): boolean {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return false;
        }
        return window.sessionStorage.getItem(SESSION_STORAGE_MUX_FORCE_CONSENT) === '1';
    } catch {
        return false;
    }
}

/**
 * Local / QA only — bypasses WM/WBD checks for Mux.
 *
 * - **Session (survives reload):** `sessionStorage.setItem('asvp_mux_force_consent', '1')` then reload.
 * - **This navigation only:** `window.__ASVP_MUX_FORCE_CONSENT__ = true` (lost on reload).
 *
 * Allowed when the bundle is non-production **or** `window.__ASVP_MUX_DEBUG__ === true`.
 */
function muxConsentForcedForLocalDebug(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    if (process.env.NODE_ENV === 'production' && window.__ASVP_MUX_DEBUG__ !== true) {
        return false;
    }
    return (
        window.__ASVP_MUX_FORCE_CONSENT__ === true || readMuxForceConsentFromSession()
    );
}

/** Analytics consent categories used for GDPR (OneTrust-style) */
const ANALYTICS_CONSENT_CATEGORIES = ['ads-general', 'behavior-general', 'perf-general'];

type WbdUserConsent = NonNullable<NonNullable<Window['WBD']>['UserConsent']>;
type WmUserConsent = NonNullable<NonNullable<Window['WM']>['UserConsent']>;

function wbdAllowsMux(wbd: WbdUserConsent): boolean {
    if (typeof wbd.getAckTermsNeeded === 'function' && wbd.getAckTermsNeeded()) {
        return false;
    }
    if (typeof wbd.inUserConsentState === 'function') {
        return wbd.inUserConsentState(ANALYTICS_CONSENT_CATEGORIES);
    }
    if (typeof wbd.isReady === 'function' && !wbd.isReady()) {
        return false;
    }
    // WBD sometimes exposes isReady() but not inUserConsentState; when ready and LTP is not
    // blocking, allow Mux (same signal as player-web after terms ack).
    if (typeof wbd.isReady === 'function' && wbd.isReady()) {
        return true;
    }
    return false;
}

/**
 * WM consent for Mux when not in the GDPR-only path (handled in shouldEnableMux).
 */
function wmAllowsMuxNonGdpr(wm: WmUserConsent): boolean {
    if (typeof wm.getRegion === 'function' && wm.getRegion() === 'ccpa') {
        if (typeof wm.getUserConsentAdvertisingState === 'function') {
            return wm.getUserConsentAdvertisingState();
        }
    }

    if (typeof wm.inUserConsentState === 'function') {
        return wm.inUserConsentState(ANALYTICS_CONSENT_CATEGORIES);
    }

    return false;
}

/**
 * Check if Mux Data analytics should be enabled based on user consent.
 * Returns false when consent is explicitly denied or not yet given.
 *
 * - GDPR: WM only (strict; do not OR with WBD)
 * - Otherwise: WM **or** WBD may grant (see module comment)
 */
export function shouldEnableMux(): boolean {
    const win = typeof window !== 'undefined' ? window : undefined;
    if (!win) return false;

    if (muxConsentForcedForLocalDebug()) {
        return true;
    }

    const wm = win.WM?.UserConsent;
    const wbd = win.WBD?.UserConsent;

    // Player-web (VideoController): only load Mux after LTP consent — while getAckTermsNeeded()
    // is true, terms are still pending. Must run before wmAllowsMuxNonGdpr so inUserConsentState
    // cannot enable Mux while LTP is outstanding.
    if (wm && typeof wm.getAckTermsNeeded === 'function' && wm.getAckTermsNeeded()) {
        return false;
    }

    if (wm) {
        if (typeof wm.getRegion === 'function' && wm.getRegion() === 'gdpr') {
            if (typeof wm.inUserConsentState === 'function') {
                return wm.inUserConsentState(ANALYTICS_CONSENT_CATEGORIES);
            }
            return false;
        }
    }

    const wmMux = wm ? wmAllowsMuxNonGdpr(wm) : false;
    const wbdMux = wbd ? wbdAllowsMux(wbd) : false;

    return wmMux || wbdMux;
}

/**
 * Short explanation when {@link shouldEnableMux} is false (for debug logs). Empty string if Mux is allowed.
 * Mirrors the decision order in `shouldEnableMux`.
 */
export function getMuxConsentDenialHint(): string {
    if (muxConsentForcedForLocalDebug()) {
        return '';
    }
    if (shouldEnableMux()) {
        return '';
    }
    const win = typeof window !== 'undefined' ? window : undefined;
    if (!win) {
        return 'no window';
    }

    const wm = win.WM?.UserConsent;
    const wbd = win.WBD?.UserConsent;

    if (!wm && !wbd) {
        return 'No WM.UserConsent or WBD.UserConsent — embed/local page often has no CMP; test on adultswim.com (or inject consent mocks)';
    }

    if (wm && typeof wm.getAckTermsNeeded === 'function' && wm.getAckTermsNeeded()) {
        return 'WM.getAckTermsNeeded() is true — acknowledge LTP/terms before Mux can run';
    }

    if (wm && typeof wm.getRegion === 'function' && wm.getRegion() === 'gdpr') {
        if (typeof wm.inUserConsentState === 'function') {
            if (!wm.inUserConsentState(ANALYTICS_CONSENT_CATEGORIES)) {
                return 'GDPR: WM.inUserConsentState(ads/behavior/perf) is false — accept analytics in CMP';
            }
        }
        return 'GDPR: WM.inUserConsentState missing';
    }

    const wmMux = wm ? wmAllowsMuxNonGdpr(wm) : false;
    const wbdMux = wbd ? wbdAllowsMux(wbd) : false;

    if (!wmMux && !wbdMux) {
        if (wbd && typeof wbd.getAckTermsNeeded === 'function' && wbd.getAckTermsNeeded()) {
            return 'WBD.getAckTermsNeeded() is true — acknowledge terms (LTP) first';
        }
        if (wbd && typeof wbd.isReady === 'function' && !wbd.isReady()) {
            return 'WBD.UserConsent.isReady() is false — wait for consent stack to hydrate';
        }
        if (!wm) {
            return 'Only WBD present and it does not grant analytics categories yet';
        }
        if (!wbd) {
            return 'Only WM present and non-GDPR path does not grant analytics (CCPA/WM.inUserConsentState)';
        }
        return 'Neither WM nor WBD grants analytics categories for Mux';
    }

    return '';
}

/**
 * Safe snapshot for debugging (never throws). Does not log by itself.
 * Used by `muxDebug` (`[asvp mux]` console output).
 */
export function getMuxConsentSnapshotForDebug(): Record<string, unknown> {
    const win = typeof window !== 'undefined' ? window : undefined;
    if (!win) {
        return { noWindow: true };
    }
    const wm = win.WM?.UserConsent;
    const wbd = win.WBD?.UserConsent;
    const snap: Record<string, unknown> = {
        hasWMUserConsent: !!wm,
        hasWBDUserConsent: !!wbd,
    };

    const safe = (fn: () => unknown): unknown => {
        try {
            return fn();
        } catch (e) {
            return e instanceof Error ? e.message : String(e);
        }
    };

    if (wm) {
        if (typeof wm.getAckTermsNeeded === 'function') {
            snap.wmAckTermsNeeded = safe(() => wm.getAckTermsNeeded!());
        }
        if (typeof wm.getRegion === 'function') {
            snap.wmRegion = safe(() => wm.getRegion!());
        }
        if (typeof wm.inUserConsentState === 'function') {
            snap.wmInAnalyticsCategories = safe(() =>
                wm.inUserConsentState!(ANALYTICS_CONSENT_CATEGORIES)
            );
        }
        if (typeof wm.getUserConsentAdvertisingState === 'function') {
            snap.wmAdvertisingConsent = safe(() => wm.getUserConsentAdvertisingState!());
        }
    }
    if (wbd) {
        if (typeof wbd.getAckTermsNeeded === 'function') {
            snap.wbdAckTermsNeeded = safe(() => wbd.getAckTermsNeeded!());
        }
        if (typeof wbd.isReady === 'function') {
            snap.wbdIsReady = safe(() => wbd.isReady!());
        }
        if (typeof wbd.inUserConsentState === 'function') {
            snap.wbdInAnalyticsCategories = safe(() =>
                wbd.inUserConsentState!(ANALYTICS_CONSENT_CATEGORIES)
            );
        }
    }
    snap.consentForcedForDebug = muxConsentForcedForLocalDebug();
    snap.muxForceConsentSession = readMuxForceConsentFromSession();
    snap.shouldEnableMux = shouldEnableMux();
    snap.denialHint = getMuxConsentDenialHint();
    return snap;
}

/** Same cleanup as player-web VideoController when Mux is attached after LTP consent. */
export function clearLtpConsentTrackPending(): void {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem('ltp-consent-track-pending');
        }
    } catch {
        // noop
    }
}
